const CAP48 = {
  "id": 215,
  "title": "Problemas de Escalabilidad de Bitcoin",
  "slug": "escalabilidad-de-bitcoin",
  "type": "lesson",
  "category": "bitcoin-basico",
  "level": "basic",
  "preview": "Bitcoin es una red extremadamente segura y descentralizada, pero enfrenta limitaciones inherentes a su diseño inicial. Estas limitaciones, conocidas como problemas de escalabilidad, afectan la velocidad, el volumen de transacciones y la eficiencia de la red.",
  "sections": [
    {
      "heading": "Introducción",
      "text": [
        "Bitcoin fue creado para ser un sistema de dinero electrónico global, descentralizado y resistente a la censura. Sin embargo, su diseño prioriza la seguridad sobre el rendimiento, lo que genera desafíos cuando la demanda de uso crece.",
        "El término 'escalabilidad' se refiere a la capacidad de una red para manejar más usuarios y más transacciones sin afectar su funcionalidad. En Bitcoin, este concepto es crucial para su expansión como sistema financiero global."
      ]
    },

    {
      "heading": "¿Qué limita la escalabilidad de Bitcoin?",
      "text": [
        "1. Tamaño limitado del bloque: cada bloque de Bitcoin puede contener aproximadamente 1 MB de información. Esto restringe el número de transacciones que pueden procesarse cada 10 minutos.",
        "Como consecuencia, la capacidad máxima teórica de Bitcoin es de entre 4 y 7 transacciones por segundo, muy inferior a sistemas tradicionales como Visa o Mastercard.",
        "2. Intervalos fijos de producción de bloques: Bitcoin genera un bloque nuevo cada 10 minutos en promedio. Aunque este ritmo es esencial para la seguridad del protocolo, también limita la velocidad con la que las transacciones son confirmadas.",
        "Aumentar esta velocidad implicaría riesgos de seguridad, como mayor probabilidad de bifurcaciones temporales.",
        "3. Propagación lenta de bloques: cuando un minero encuentra un bloque, debe transmitirlo al resto de la red. Si los bloques fueran demasiado grandes, tardarían más tiempo en propagarse, lo que aumentaría la probabilidad de bloques huérfanos.",
        "Este fenómeno afectaría la descentralización porque favorecería a los mineros con mejor conectividad.",
        "4. Seguridad frente a velocidad: Bitcoin sacrifica velocidad en favor de la seguridad. Todas las transacciones deben cumplir estrictos requisitos criptográficos y de consenso, lo que añade complejidad al proceso de validación.",
        "La robustez del sistema depende precisamente de este enfoque conservador."
      ]
    },

    {
      "heading": "Consecuencias de la falta de escalabilidad",
      "text": [
        "1. Aumento de las comisiones: cuando la red se congestiona, los usuarios compiten por espacio en el bloque, elevando las tarifas.",
        "En periodos de alta demanda, las comisiones han llegado a superar los 20 o 30 dólares por transacción.",
        "2. Tiempos de espera prolongados: si un usuario paga una comisión baja, su transacción puede quedar retenida durante horas dentro del mempool.",
        "Esto afecta especialmente a pagos pequeños o de uso cotidiano.",
        "3. Limitaciones para pagos minoristas: las tiendas y servicios requieren transacciones rápidas y de bajo costo. Sin soluciones adicionales, Bitcoin en capa 1 no es ideal para el comercio diario.",
        "De aquí surge la necesidad de soluciones como Lightning Network."
      ]
    },

    {
      "heading": "Soluciones propuestas para la escalabilidad",
      "text": [
        "1. SegWit (Segregated Witness): SegWit reorganiza la estructura de la transacción moviendo las firmas fuera del bloque principal.",
        "Esto reduce el tamaño efectivo de cada transacción, permitiendo incluir más operaciones por bloque y habilitando futuras optimizaciones como Lightning Network.",
        "2. Lightning Network (Capa 2): Lightning es una red paralela que permite pagos instantáneos y casi gratuitos.",
        "Funciona mediante canales de pago que se abren y cierran en la blockchain, pero permiten miles de transacciones fuera de ella. Lightning Network puede escalar Bitcoin a millones de transacciones por segundo.",
        "3. Sidechains: son cadenas de bloques paralelas conectadas a Bitcoin que permiten ejecutar funciones más complejas sin saturar la red principal.",
        "Ejemplos de sidechains son RSK y Liquid, que ofrecen smart contracts, transacciones rápidas y emisión de activos sin modificar Bitcoin Core.",
        "4. Mejoras criptográficas (Taproot, Schnorr): estas actualizaciones permiten transacciones más compactas y eficientes.",
        "Las firmas Schnorr permiten combinar varias firmas en una sola, reduciendo el espacio ocupado. Taproot mejora la privacidad y reduce costos para ciertos tipos de transacciones complejas."
      ]
    },

    {
      "heading": "Tabla comparativa: Capa 1 (L1) vs Capa 2 (L2)",
      "table": {
        "columns": [
          "Aspecto",
          "Capa 1 (L1) - Bitcoin base",
          "Capa 2 (L2) - Lightning / soluciones off-chain"
        ],
        "rows": [
          [
            "Ubicación de las transacciones",
            "Se registran directamente en la blockchain principal de Bitcoin.",
            "Se realizan fuera de la blockchain principal y solo se anclan cuando se abren o cierran canales o se consolidan operaciones."
          ],
          [
            "Seguridad",
            "Seguridad máxima, protegida directamente por la prueba de trabajo (minería) de Bitcoin.",
            "Hereda la seguridad de la capa 1, pero depende del correcto funcionamiento de los canales y protocolos de capa 2."
          ],
          [
            "Velocidad de transacción",
            "Limitada por el tamaño de bloque y el intervalo de 10 minutos. De 4 a 7 tx/s aproximadamente.",
            "Pagos casi instantáneos, capaces de manejar miles o millones de transacciones por segundo."
          ],
          [
            "Coste de las comisiones",
            "Comisiones más altas, especialmente en periodos de congestión de la red.",
            "Comisiones muy bajas o casi nulas por cada pago dentro de los canales."
          ],
          [
            "Casos de uso típicos",
            "Transacciones de alto valor, liquidaciones finales, movimientos a largo plazo.",
            "Pagos cotidianos, micropagos, comercio minorista y uso diario."
          ],
          [
            "Complejidad para el usuario",
            "Modelo simple: enviar y recibir a direcciones en la blockchain.",
            "Requiere entender canales, capacidad de los canales y, en ocasiones, gestión de rutas de pago."
          ],
          [
            "Objetivo principal",
            "Preservar la máxima seguridad y descentralización del sistema.",
            "Escalar el uso de Bitcoin para un gran volumen de transacciones rápidas y baratas."
          ]
        ]
      }
    },

    {
      "heading": "¿Puede Bitcoin escalar en el futuro?",
      "text": [
        "Sí. Aunque la capa base (L1) está limitada por diseño, las soluciones de segunda capa y las optimizaciones continuas permiten que Bitcoin aumente su capacidad sin sacrificar su seguridad.",
        "La comunidad trabaja constantemente en nuevas mejoras, pero siempre priorizando la descentralización.",
        "El enfoque modular —Bitcoin como base segura y capas superiores para el alto rendimiento— es la vía que más consenso ha generado."
      ]
    },

    {
      "heading": "Conclusión",
      "text": [
        "Los problemas de escalabilidad de Bitcoin no significan que la red sea ineficiente, sino que fue diseñada con prioridades muy específicas: seguridad, descentralización y resistencia a la censura.",
        "Las limitaciones actuales son ampliamente conocidas y se están abordando mediante soluciones de capa 2, optimizaciones de protocolo y desarrollo continuo.",
        "Bitcoin evoluciona sin perder sus principios fundamentales, preparándose para soportar millones de usuarios en un futuro globalizado."
      ]
    }
  ]
};

export default CAP48;
